import { Box, Typography, useTheme } from '@mui/material';
import { colorWhite, colorBorderLight } from 'customColors';
import { useProfile, useRequireAuth, useRequireRole } from 'hooks';
import React, { ReactElement, useEffect, useState } from 'react';
import {
  DetailsTab,
  EventSetupTab,
  FacilitiesTab,
  LearningOutcomesTab,
} from 'components/pages/admin/tabs';
import { useRouter } from 'next/router';
import { AdminPageLayout } from '../../layouts';

const TABS = [
  'facilities',
  'event-setup',
  'learning-outcomes',
  'details',
] as const;

type Tab = typeof TABS[number];

export const MAX_IMAGE_FILE_SIZE_MB = 5;

const TAB_LABELS: Record<Tab, string> = {
  facilities: 'Facilities',
  'event-setup': 'Event Settings',
  'learning-outcomes': 'Learning Outcomes',
  details: 'Branding',
};

const isTab = (value: unknown): value is Tab =>
  typeof value === 'string' && (TABS as readonly string[]).includes(value);

export default function AdminDetailsPage (): ReactElement {
  useRequireAuth();
  useRequireRole('admin');

  const theme = useTheme();
  const router = useRouter();
  const { profile } = useProfile();
  const schoolId = profile?.school?.id;
  const isMultiStepApprovalEnabled =
    profile?.school?.isMultiStepApprovalEnabled;

  const [selectedTab, setSelectedTab] = useState<Tab>('details');

  useEffect(() => {
    const tabQuery = router.query.tab;
    const tab = Array.isArray(tabQuery) ? tabQuery[0] : tabQuery;
    if (isTab(tab)) {
      if (tab === 'event-setup' && !isMultiStepApprovalEnabled) return;
      setSelectedTab(tab);
    }
  }, [router.query.tab, isMultiStepApprovalEnabled]);

  const renderTab = (tab: Tab) => {
    const isActive = selectedTab === tab;
    return (
      <Box
        key={tab}
        role="tab"
        aria-selected={isActive}
        onClick={() => setSelectedTab(tab)}
        tabIndex={0}
        onKeyPress={(e: React.KeyboardEvent) =>
          e.key === ' ' && setSelectedTab(tab)
        }
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '12px 24px',
          borderRadius: '8px',
          border: `1px solid ${
            isActive ? theme.palette.primary.main : colorBorderLight
          }`,
          backgroundColor: isActive ? theme.palette.primary.main : colorWhite,
          color: isActive ? colorWhite : theme.palette.primary.main,
          cursor: isActive ? 'default' : 'pointer',
          userSelect: 'none',
          whiteSpace: 'nowrap',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: theme.palette.primary.main,
          },
        }}
      >
        <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>
          {TAB_LABELS[tab]}
        </Typography>
      </Box>
    );
  };

  if (!schoolId) {
    return (
      <AdminPageLayout title="School Settings" active="school details">
        <Box mt={20} textAlign="center">
          <Typography>No school associated with this account.</Typography>
        </Box>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout title="School Settings" active="school details">
      <Box mt="16px" maxWidth={950}>
        <Box
          sx={{
            display: 'flex',
            gap: '8px',
            overflowX: 'auto',
            pb: 1,
            '&::-webkit-scrollbar': {
              height: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: '3px',
            },
            '@media (min-width: 768px)': {
              overflowX: 'visible',
            },
          }}
        >
          {TABS.filter(
            tab => tab !== 'event-setup' || isMultiStepApprovalEnabled,
          ).map(renderTab)}
        </Box>
      </Box>

      {schoolId && (
        <Box mt="32px">
          {selectedTab === 'facilities' && (
            <FacilitiesTab schoolId={schoolId} />
          )}
          {selectedTab === 'event-setup' && isMultiStepApprovalEnabled && (
            <EventSetupTab schoolId={schoolId} />
          )}
          {selectedTab === 'learning-outcomes' && (
            <LearningOutcomesTab schoolId={schoolId} />
          )}
          {selectedTab === 'details' && <DetailsTab schoolId={schoolId} />}
        </Box>
      )}
    </AdminPageLayout>
  );
}
